import useCallCenterStore from '@/stores/callcenter';
import { useShallow } from 'zustand/react/shallow';

type ToneStep = {
  frequency: number;
  startOffset: number;
  duration: number;
  peakGain: number;
};

// Lazily created once per page load and reused for every chime after that --
// browsers rate-limit how many AudioContexts a page may keep open, and
// jsdom (the test environment) has no AudioContext constructor at all, which
// is exactly what the guard in getAudioContext() below is for.
let sharedAudioContext: AudioContext | null = null;

const getAudioContext = (): AudioContext | null => {
  if (typeof window === 'undefined' || typeof window.AudioContext === 'undefined') {
    return null;
  }

  if (!sharedAudioContext) {
    sharedAudioContext = new window.AudioContext();
  }

  return sharedAudioContext;
};

// A hard on/off gain switch produces an audible "click" at the start and end
// of a tone -- ramping up to `peakGain` and back down to 0 avoids it.
const playTone = (
  context: AudioContext,
  { frequency, startOffset, duration, peakGain }: ToneStep,
) => {
  const oscillator = context.createOscillator();
  const gainNode = context.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.value = frequency;

  const startTime = context.currentTime + startOffset;
  const endTime = startTime + duration;

  gainNode.gain.setValueAtTime(0, startTime);
  gainNode.gain.linearRampToValueAtTime(peakGain, startTime + duration * 0.2);
  gainNode.gain.linearRampToValueAtTime(0, endTime);

  oscillator.connect(gainNode);
  gainNode.connect(context.destination);

  oscillator.start(startTime);
  oscillator.stop(endTime);
};

// Autoplay restrictions, a missing/unavailable audio backend, or a user
// gesture requirement can all make the Web Audio API throw -- a missed
// notification chime is harmless, but an unhandled throw from inside a
// realtime event handler is not, so nothing here is allowed to escape.
const playSequence = (steps: ToneStep[]): void => {
  try {
    const context = getAudioContext();
    if (!context) return;

    // Chimes fire from realtime handlers -- never from a user gesture -- so
    // browsers hand us this context 'suspended' on first construction, and
    // without a resume() every chime for the rest of the session would be
    // silently inaudible (construction doesn't throw, so the catch below
    // never learns about it). resume() is fire-and-forget: the browser
    // honors it once ANY prior user gesture exists on the page, and tones
    // scheduled below play as soon as it completes. The one chime that
    // arrives before the agent's very first interaction with the page is
    // unrecoverable by design -- it stays silent, and every later chime
    // plays. The .catch keeps an autoplay-policy rejection from surfacing
    // as an unhandled promise rejection (the try/catch around this block
    // only covers synchronous throws).
    if (context.state === 'suspended') {
      context.resume().catch(() => undefined);
    }

    steps.forEach((step) => playTone(context, step));
  } catch {
    // Intentionally swallowed -- see comment above.
  }
};

const ASSIGNED_TONES: ToneStep[] = [
  { frequency: 880, startOffset: 0, duration: 0.12, peakGain: 0.2 },
  { frequency: 1175, startOffset: 0.12, duration: 0.12, peakGain: 0.2 },
];

const NUDGE_TONES: ToneStep[] = [
  { frequency: 660, startOffset: 0, duration: 0.15, peakGain: 0.12 },
];

export const useNotificationSound = () => {
  const { soundMuted } = useCallCenterStore(
    useShallow((state) => ({ soundMuted: state.soundMuted })),
  );

  const playAssigned = () => {
    // Muted means zero AudioContext work, full stop -- not "construct it and
    // play at zero gain".
    if (soundMuted) return;
    playSequence(ASSIGNED_TONES);
  };

  const playNudge = () => {
    if (soundMuted) return;
    playSequence(NUDGE_TONES);
  };

  return { playAssigned, playNudge };
};
